import React, { useState } from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  savedSearchListHeaderSx,
  savedSearchListRowSx,
  savedSearchListScrollSx,
} from "./savedSearchListTableSx";
import MobileActionSheet from "../../components/mobile/MobileActionSheet";
import useIsMobile from "../../hooks/useIsMobile";
import {
  MOBILE_LIST_ROW_MIN_HEIGHT_PX,
  MOBILE_TOUCH_MIN_PX,
  MOBILE_TYPE,
} from "../../theme/mobileLayout";

/** Match EnrichmentGrid V2 spreadsheet density — fixed, not stretched. */
const ROW_HEIGHT = 44;

const stopRowNavigation = (event) => {
  event.stopPropagation();
};

function formatCreatedAt(value, { short = false } = {}) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  if (short) {
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  return date.toLocaleString();
}

/**
 * Saved Search list — desktop table rows; mobile card/list + overflow actions.
 */
export default function SavedSearchList({
  rows = [],
  loading = false,
  resolveRowId,
  onOpen,
  onExport,
  onDelete,
  exportingId = null,
}) {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [actionRow, setActionRow] = useState(null);

  if (isMobile) {
    return (
      <Box
        data-testid="saved-search-list"
        data-mobile="true"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            rows.map((row, index) => {
              const rowId = resolveRowId(row) || String(row.name || index);
              return (
                <Box
                  key={rowId}
                  role="button"
                  tabIndex={0}
                  data-testid={`workspace-row-${rowId}`}
                  onClick={() => onOpen?.(row)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onOpen?.(row);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minHeight: MOBILE_LIST_ROW_MIN_HEIGHT_PX,
                    px: 1.5,
                    py: 1.25,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    cursor: "pointer",
                    "&:active": { bgcolor: "action.hover" },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        ...MOBILE_TYPE.cardTitle,
                        color: "text.primary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.name}
                    </Typography>
                    <Typography sx={{ ...MOBILE_TYPE.helper, color: "text.secondary", mt: 0.35 }}>
                      {`${row.record_type === "saved_search" ? "Live query" : `${row.company_count ?? 0} companies`} · ${formatCreatedAt(row.created_at, { short: true })}`}
                    </Typography>
                  </Box>
                  <IconButton
                    aria-label={`Actions for ${row.name}`}
                    onClick={(event) => {
                      stopRowNavigation(event);
                      setActionRow({ row, rowId });
                    }}
                    sx={{ minWidth: MOBILE_TOUCH_MIN_PX, minHeight: MOBILE_TOUCH_MIN_PX }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              );
            })
          )}
        </Box>

        <MobileActionSheet
          open={Boolean(actionRow)}
          onClose={() => setActionRow(null)}
          title={actionRow?.row?.name || "Actions"}
          actions={[
            {
              id: "export",
              label: "Download CSV",
              icon: <FileDownloadIcon fontSize="small" />,
              disabled: actionRow && exportingId === actionRow.rowId,
              onClick: () => onExport?.(actionRow.row),
            },
            {
              id: "delete",
              label: "Permanently delete",
              icon: <DeleteIcon fontSize="small" />,
              destructive: true,
              onClick: () => onDelete?.(actionRow.rowId),
            },
          ]}
        />
      </Box>
    );
  }

  return (
    <Box
      data-testid="saved-search-list"
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Box
        role="row"
        aria-label="Saved search column headers"
        sx={savedSearchListHeaderSx(theme)}
      >
        <Typography component="span" sx={{ fontWeight: 700, fontSize: 13 }}>
          Name
        </Typography>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: 13 }}>
          Companies
        </Typography>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: 13 }}>
          Created
        </Typography>
        <Typography
          component="span"
          sx={{ fontWeight: 700, fontSize: 13, textAlign: "right" }}
        >
          Actions
        </Typography>
      </Box>

      <Box sx={savedSearchListScrollSx(theme)}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : (
          rows.map((row, index) => {
            const rowId = resolveRowId(row) || String(row.name || index);
            return (
              <Box
                key={rowId}
                role="button"
                tabIndex={0}
                data-testid={`workspace-row-${rowId}`}
                data-row-height={ROW_HEIGHT}
                onClick={() => onOpen?.(row)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpen?.(row);
                  }
                }}
                sx={savedSearchListRowSx(theme, index, ROW_HEIGHT)}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 550,
                    color: "text.primary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                    "&:hover": {
                      textDecoration: "underline",
                      color: "primary.main",
                    },
                  }}
                >
                  {row.name}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "text.primary", lineHeight: 1.2 }}>
                      {row.record_type === "saved_search" ? "Live query" : row.company_count ?? 0}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "text.primary", lineHeight: 1.2 }}>
                  {formatCreatedAt(row.created_at)}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 0.25,
                  }}
                  onClick={stopRowNavigation}
                  onMouseDown={stopRowNavigation}
                >
                  <Tooltip title="Export CSV">
                    <IconButton
                      aria-label="Export CSV"
                      onClick={(event) => {
                        stopRowNavigation(event);
                        onExport?.(row);
                      }}
                      disabled={exportingId === rowId}
                      size="small"
                      sx={{
                        color: "text.secondary",
                        p: 0.5,
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      <FileDownloadIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Permanently delete">
                    <IconButton
                      aria-label={`Permanently delete ${row.name || "saved search"}`}
                      onClick={(event) => {
                        stopRowNavigation(event);
                        onDelete?.(rowId);
                      }}
                      color="error"
                      size="small"
                      sx={{ p: 0.5 }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
